import axios, { AxiosRequestConfig } from "axios";
import moment from "moment";
import { useAuthState, useRecaptcha } from "../store/store";
import { GetBusinessUsersResponse } from "./types";

export type User = {
  id: string;
  provider: string;
  providerId: string;
  username: string;
  name: string | null;
  createdAt: Date;
  updatedAt: Date;
  password: string | null;
  isVerified: boolean | null;
  roles: any;
  phone: string | null;
  email: string | null;
};

const api = () => {
  const _api = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    withCredentials: true,
    headers: {
      Authorization: `Bearer ${useAuthState.getState().token}`,
    },
  });

  _api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error.response &&
        error.response.data.message === "Low recaptcha score."
      ) {
        useRecaptcha.setState((state) => ({
          ...state,
          recaptchaFailed: true,
        }));
      }
      throw error.response.data;
    }
  );
  return _api;
};

declare const grecaptcha: any;

const getRecapthaToken = async (action: string) => {
  return new Promise<string>((resolve) => {
    grecaptcha.ready(() => {
      grecaptcha
        .execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, { action })
        .then((token: string) => {
          resolve(token);
        });
    });
  });
};

const get =
  <T>(url: string, action?: string, config: AxiosRequestConfig = {}) =>
  async () => {
    return api().get<T>(url, {
      headers: action
        ? ({ recaptcha: await getRecapthaToken(action) } as any)
        : undefined,
      ...config,
    });
  };

const _delete =
  <T>(url: string, action?: string) =>
  async () => {
    return api().delete<T>(url, {
      headers: action
        ? ({ recaptcha: await getRecapthaToken(action) } as any)
        : undefined,
    });
  };

const post =
  <T>(url: string, action?: string) =>
  async (json: T) =>
    api().post(url, json, {
      headers: action
        ? ({ recaptcha: await getRecapthaToken(action) } as any)
        : undefined,
    });

export const payMastercardOrder = post("/order/mastercard/pay", "pay");
export const initiateMastercardOrder = post(
  "/order/mastercard/initiate",
  "orderInitiate"
);
export const authenticateMastercardOrder = post(
  "/order/mastercard/authenticate",
  "orderAuthenticate"
);

export const sendContactSales = post<{ values: any }>(
  "/contact/sales",
  "contact"
);

export const me = get<User>("/auth/jwt/profile");
export const registerLocal = post<{ values: any }>("/auth/local/register");
export const loginLocal = post<{ email: string; password: string }>(
  "/auth/local/login"
);
export const logout = post("/auth/jwt/logout");
export const verifyRegistration = post<{ token: string }>("/auth/local/verify");

export const getEvents = get("/events", "events"); //FrontPage
export const getUserId = get("/user-id", "userId");

export const getCheckout = (id: string) => get(`/checkout/${id}`, "checkout");

export const sendMtnRwandaOrder = post("/order/mtn-rwanda", "order");
export const getMtnRwandaPaymentStatus = get(
  "/order/mtn-rwanda/payment-status",
  "order"
);

export const sendOrangeSonatelOrder = post("/order/orange-sonatel", "order");
export const getOrangeSonatelPaymentStatus = get(
  "/order/orange-sonatel/payment-status",
  "order"
);

export const sendAirtelAfricaOrder = post("/order/airtelafrica", "order");
export const getAirtelAfricaPaymentStatus = get(
  "/order/airtelafrica/payment-status",
  "order"
);

export const sendFreeSenegalOrder = post("/order/freesenegal", "order");
export const getFreeSenegalPaymentStatus = get(
  "/order/freesenegal/payment-status",
  "order"
);

//admin

export const userGet = (id: string) => get(`/auth/local/user/${id}`, "userGet");
export const userEdit = post<{ values: any }>("/auth/local/edit-user");
export const userDelete = (id: string) => {
  return _delete(`/auth/local/delete-user/${id}`);
};
export const businessUsers = (id: string) => {
  return get<GetBusinessUsersResponse>(`/admin/business-users/${id}`);
};
//roles
export const usersWithBusinessRoles = (id: string) => {
  return get<GetBusinessUsersResponse>(`/admin/business-roles/${id}`);
};
export const rolesEdit = post<{ values: any }>("/admin/edit-roles");

export const businessesGet = () => {
  return get("/admin/businesses");
};

export const businessGet = (id: string) => {
  return get(`/admin/business/${id}`, "businessGet");
};

export const businessEdit = post<{ values: any }>("/admin/edit-business");
export const businessDelete = (id: string) => {
  return _delete(`/admin/delete-business/${id}`);
};
// Operators
export const operatorsGet = (id: string) => {
  return get(`/admin/operators/${id}`);
};

export const operatorEdit = post<{ values: any }>("/admin/edit-operator");

export const operatorDelete = (id: string) => {
  return _delete(`/admin/delete-operator/${id}`);
};

// Event type
export const eventTypesGet = (id: number) => {
  return get(`/admin/event-types/${id}`);
};

export const eventTypeEdit = post<{ values: any }>("/admin/edit-event-type");

export const eventTypeDelete = (id: number) => {
  return _delete(`/admin/delete-event-type/${id}`);
};

// Event
export const eventEditOptionsGet = (id: number) => {
  return get(`/admin/event-options/${id}`);
};

export const eventsGet = (
  id: number,
  dateRange: {
    start: Date;
    end: Date;
  }
) => {
  return get(
    `/admin/events/${id}?start=${moment(dateRange.start).format(
      "YYYYMMDD"
    )}&end=${moment(dateRange.end).format("YYYYMMDD")}`
  );
};

export const adminEventGet = (id: number) => {
  return get(`/admin/event/${id}`);
};

export const eventEdit = post<{ values: any }>("/admin/edit-event");

export const eventDelete = (id: number) => {
  return _delete(`/admin/delete-event/${id}`);
};

//Ticket type set
export const ticketTypeSetsGet = (id: number) => {
  return get(`/admin/ticket-type-sets/${id}`);
};

export const ticketTypeSetEdit = post<{ values: any }>(
  "/admin/edit-ticket-type-set"
);

export const ticketTypeSetDelete = (id: number) => {
  return _delete(`/admin/delete-ticket-type-set/${id}`);
};

//Venues venues
export const venuesGet = (id: number) => {
  return get(`/admin/venues/${id}`);
};

export const venueGet = (id: string) => {
  return get(`/admin/venue/${id}`);
};

export const venueEdit = post<{ values: any }>("/admin/edit-venue");

export const venueDelete = (id: string) => {
  return _delete(`/admin/delete-venue/${id}`);
};

//venue part
export const venuePartEdit = post<{ values: any }>("/admin/edit-venue-part");

export const venuePartDelete = (id: string) => {
  return _delete(`/admin/delete-venue-part/${id}`);
};

export const checkoutsGet = (
  id: number,
  dateRange: {
    start: Date | null;
    end: Date | null;
  },
  searchTerm: string
) => {
  return get(
    `/admin/checkouts/${id}?start=${
      dateRange.start ? moment(dateRange.start).format("YYYYMMDD") : "null"
    }&end=${
      dateRange.end ? moment(dateRange.end).format("YYYYMMDD") : "null"
    }&t=${searchTerm}`
  );
};

export const checkoutGet = (id: number) => {
  return get(`/admin/checkout/${id}`);
};

export const refundCreate = post<{ amount: number; checkoutSessionId: string }>(
  "/admin/create-refund"
);

export const refundAuthorization = post<{
  id: string;
  authorizationCode: string;
}>("/admin/refund-authorization");

/* 
export const checkoutSearch = (id: string, searchTerm: string) => {
  return get(`/admin/checkout-search/${id}?t=${searchTerm}`);
};
 */
export const dashboardGet = (
  id: string,
  dateRange: {
    start: Date;
    end: Date;
  },
  operatorId: string,
  comparisonId: string
) => {
  return get(
    `/admin/dashboard/${id}?start=${moment(dateRange.start).format(
      "YYYYMMDD"
    )}&end=${moment(dateRange.end).format(
      "YYYYMMDD"
    )}&operatorid=${operatorId}&tstart=${moment()
      .startOf("day")
      .format("YYYYMMDD")}&comparisonid=${comparisonId}`
  );
};

export const summaryGet = (
  id: string,
  dateRange: {
    start: Date;
    end: Date;
  },
  operatorId: string,
  comparisonId: string
  // eslint-disable-next-line sonarjs/no-identical-functions
) => {
  return get(
    `/admin/summary/${id}?start=${moment(dateRange.start).format(
      "YYYYMMDD"
    )}&end=${moment(dateRange.end).format(
      "YYYYMMDD"
    )}&operatorid=${operatorId}&tstart=${moment()
      .startOf("day")
      .format("YYYYMMDD")}&
      comparisonid=${comparisonId}`
  );
};

export const pdfInvoiceGet = async (id: string) => {
  const response = await get(`/admin/pdf-invoice/${id}`, undefined, {
    responseType: "blob",
  })();
  const blob = response.data as Blob;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  //get name from content disposition

  console.log(response.headers);

  const contentDisposition = response.headers["content-disposition"];

  const fileName = contentDisposition
    .split(";")
    .find((item) => item.includes("filename"))
    .split("=")[1];
  link.setAttribute("download", fileName);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getUserAdminOfBusinesses = get<
  {
    name: string;
    id: string;
  }[]
>("/admin/user-admin-of-businesses", "getUserAdminOfBusinesses");
export const usersAdminGet = (businessId: string) =>
  get(`/admin/users/${businessId}`);

export const eventLocationsGet = () => {
  return get(`/event-locations`);
};
export interface GetFilteredEventsOptions {
  start: Date;
  end: Date;
  categories: string[];
  location: string;
  latitude: number;
  longitude: number;
}

export const getFilteredEvents = (
  options: GetFilteredEventsOptions = {
    start: moment().toDate(),
    end: moment().endOf("day").toDate(),
    categories: [],
    location: "Rwanda",
    latitude: 0,
    longitude: 0,
  }
) => {
  return get<any[]>("/events", "events", {
    params: {
      start: options.start,
      end: options.end,
      categories: options.categories.join(","),
      location: options.location,
      latitude: options.latitude,
      longitude: options.longitude,
    },
  });
};

export const postGoogleOauthCode = post<{ code: string }>(
  "/auth/google/exchange"
);
